import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { googleAuth, NeedsRegistrationResponse } from '../api/auth';
import GradeSelectionModal from '../components/GradeSelectionModal';
import '../styles/global.css';

// Type declaration for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, config: { theme: string; size: string; text: string; width?: number }) => void;
        };
      };
    };
  }
}

const Home: React.FC = () => {
  const { user, login, loginWithToken, register, loading } = useAuth();
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [grade, setGrade] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Google SSO state
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [googleEmail, setGoogleEmail] = useState('');
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    setError('');
    setIsGoogleSubmitting(true);

    try {
      const result = await googleAuth(response.credential);

      if ('needs_registration' in result && result.needs_registration) {
        const regData = result as NeedsRegistrationResponse;
        setSessionToken(regData.session_token);
        setGoogleEmail(regData.email);
        setShowGradeModal(true);
        setIsGoogleSubmitting(false);
      } else if ('access_token' in result) {
        await loginWithToken(result.access_token);
        navigate('/account');
      }
    } catch (err: any) {
      setIsGoogleSubmitting(false);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;

      if (status === 401) {
        setError('Could not sign in with Google. Please try again.');
      } else if (status === 503) {
        setError('Authentication service temporarily unavailable. Please try again.');
      } else {
        setError(detail || 'An error occurred with Google sign-in. Please try again.');
      }
    }
  }, [loginWithToken, navigate]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !googleButtonRef.current) return;

    // Wait for GSI script to load
    const initializeGSI = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCallback,
        });

        window.google.accounts.id.renderButton(
          googleButtonRef.current!,
          { theme: 'outline', size: 'large', text: 'signin_with', width: 400 }
        );
      }
    };

    // GSI script might already be loaded or still loading
    if (window.google?.accounts?.id) {
      initializeGSI();
    } else {
      // Poll for script load (script is loaded async)
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          initializeGSI();
        }
      }, 100);

      // Stop polling after 10 seconds
      const timeout = setTimeout(() => clearInterval(interval), 10000);
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [handleGoogleCallback]);

  const handleGradeSelect = async (selectedGrade: number) => {
    setIsGoogleSubmitting(true);
    setError('');

    try {
      const result = await googleAuth(undefined, selectedGrade, sessionToken);

      if ('access_token' in result) {
        setShowGradeModal(false);
        await loginWithToken(result.access_token);
        navigate('/account');
      }
    } catch (err: any) {
      setIsGoogleSubmitting(false);
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to create account. Please try again.');
      setShowGradeModal(false);
    }
  };

  const handleGradeCancel = () => {
    setShowGradeModal(false);
    setSessionToken('');
    setGoogleEmail('');
    setIsGoogleSubmitting(false);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (user) {
    return <Navigate to="/account" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isRegistering && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (username.length < 3 || username.length > 20) {
      setError('Username must be 3-20 characters');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isRegistering && grade === null) {
      setError('Please select your grade');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isRegistering) {
        await register(username, password, grade!);
      } else {
        await login(username, password);
      }
      navigate('/account');
    } catch (err: any) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setPassword('');
    setConfirmPassword('');
    setGrade(null);
  };

  return (
    <div className="container">
      <div className="card">
        <h1 className="title">Block Math</h1>
        <p className="tagline">Build your team. Level up your math skills. Collect characters and conquer challenges from kindergarten through 5th grade.</p>
        <h2 className="subtitle">{isRegistering ? 'Create Account' : 'Login'}</h2>

        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={isSubmitting}
              required
            />
          </div>

          {isRegistering && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                disabled={isSubmitting}
                required
              />
            </div>
          )}

          {isRegistering && (
            <div className="form-group">
              <label htmlFor="grade">Grade</label>
              <select
                id="grade"
                value={grade ?? ''}
                onChange={(e) => setGrade(e.target.value ? parseInt(e.target.value) : null)}
                disabled={isSubmitting}
                required
              >
                <option value="">Select grade</option>
                <option value="0">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
                <option value="3">3rd Grade</option>
                <option value="4">4th Grade</option>
                <option value="5">5th Grade</option>
              </select>
            </div>
          )}

          {error && <div className="error">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Please wait...' : (isRegistering ? 'Create Account' : 'Login')}
          </button>
        </form>

        {/* Google Sign-In section */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <div style={{ marginTop: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '0 0 16px 0',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
            <div
              ref={googleButtonRef}
              id="google-signin-button"
              style={{ display: 'flex', justifyContent: 'center' }}
            />
          </div>
        )}

        <div className="toggle-link">
          <button type="button" onClick={toggleMode} className="link-btn">
            {isRegistering ? 'Already have an account? Login' : 'Create Account'}
          </button>
        </div>

        <div className="toggle-link" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <button type="button" onClick={() => navigate('/manager')} className="link-btn">
            Are you a parent or teacher? Manage leagues →
          </button>
        </div>
      </div>

      {showGradeModal && (
        <GradeSelectionModal
          email={googleEmail}
          onSelect={handleGradeSelect}
          onCancel={handleGradeCancel}
          isSubmitting={isGoogleSubmitting}
        />
      )}
    </div>
  );
};

export default Home;
